import { Box, Chip, Typography } from '@mui/material';
import React, { useState } from 'react';
import useScreenSize from '../../utils/useScreenSize';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import getTextTruncationSx from '../../utils/getTextTruncationSx';
import Tooltip from '../Tooltip';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { Product } from '@klab-berlin/api-sdk/lib/types/responses/Product';
import { useTranslation } from 'react-i18next';
import CardHeader from '../cards/MuiCard/MuiCardBase/MuiCardBaseLong/MuiCardBaseLongHeader';
import NewCardDescription from '../cards/common/CardDescription/NewCardDescription';

const SummaryDetails: React.FC<{
  children: React.ReactNode;
  itemData: Partial<Product> | Partial<MindItem>;
  primaryHeaderText: string;
  secondaryHeaderText?: string;
  alignToBottom?: boolean;
}> = ({ children, itemData, primaryHeaderText, secondaryHeaderText, alignToBottom }) => {
  const { t } = useTranslation();
  const { isSmallScreen, isLargeScreen } = useScreenSize();

  const [isExpanded, setIsExpanded] = useState(false);

  const DetailsHeader = () => (
    <>
      <CardHeader
        primaryText={primaryHeaderText}
        secondaryText={secondaryHeaderText}
        showReadOnlyChip={itemData.isReadOnly}
      />
      <Typography
        variant='h5'
        sx={{ color: palette.text.primary }}
        data-testid='summary-details-title'
      >
        {itemData.title}
      </Typography>
      {itemData.subtitle && (
        <Tooltip
          label={itemData.subtitle}
          placement='bottom'
          hideWithoutOverflow={true}
        >
          <Typography
            variant='subtitle1'
            sx={{ color: palette.text.primary, ...getTextTruncationSx(2) }}
          >
            {itemData.subtitle}
          </Typography>
        </Tooltip>
      )}
    </>
  );

  const MobileDetailsHeader = () => (
    <>
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.xs,
          }}
        >
          <CardHeader
            primaryText={primaryHeaderText}
            secondaryText={secondaryHeaderText}
          />
          <Typography variant='h6' sx={{ color: palette.text.primary }}>
            {itemData.title}
          </Typography>
          <Typography
            variant='subtitle1'
            sx={{ color: palette.text.primary, ...getTextTruncationSx(2) }}
          >
            {itemData.subtitle}
          </Typography>
        </Box>
      </Box>
      {itemData.isReadOnly && (
        <Box>
          <Chip label={t('Read only')} />
        </Box>
      )}
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.sm,
        width: '100%',
      }}
    >
      {isSmallScreen ? <MobileDetailsHeader /> : <DetailsHeader />}
      <NewCardDescription
        description={itemData.description}
        lines={5}
        isExpanded={isExpanded}
        onShowToggleClick={() => setIsExpanded(!isExpanded)}
      />
      <Box
        sx={{
          display: 'inline-flex',
          flexWrap: 'wrap',
          width: '100%',
          justifyContent: 'space-between',
          marginTop: alignToBottom && isLargeScreen ? 'auto' : undefined,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default SummaryDetails;
